/**
 * @fileoverview Página responsável pelo painel operacional do superadministrador.
 */

import React, { useState, useEffect, useContext, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  InputAdornment,
  TextField,
  Chip,
  Button,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SignalWifiOffIcon from "@material-ui/icons/SignalWifiOff";
import CropFreeIcon from "@material-ui/icons/CropFree";

import { formatDistanceToNow } from "date-fns";
import ptBR from "date-fns/locale/pt-BR";

import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import { toast } from "react-toastify";
import toastError from "../../errors/toastError";
import ConfirmationModal from "../../components/ConfirmationModal";
import QrcodeModal from "../../components/QrcodeModal";

import { Search, X, RefreshCw } from "lucide-react";
import MessageStats from "../../components/MessageStats";
// Fonte Montserrat
const loadMontserratFont = () => {
  if (!document.getElementById("montserrat-font")) {
    const link = document.createElement("link");
    link.id = "montserrat-font";
    link.rel = "stylesheet";
    link.href =
      "https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
  }
};

const useStyles = makeStyles((theme) => ({
  root: {
    height: "100vh",
    overflowY: "auto",
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(135deg, #0a0a0b 0%, #121213 50%, #0a0a0b 100%)"
        : "linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)",
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
    fontFamily: "'Montserrat', sans-serif !important",
    "& *": { fontFamily: "'Montserrat', sans-serif !important" },
    ...theme.scrollbarStyles,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(4),
  },
  title: {
    fontSize: "1.8rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
  },
  subtitle: {
    fontSize: "0.85rem",
    color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
    marginTop: 2,
  },
  cardsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
    },
  },
  card: {
    background:
      theme.palette.type === "dark"
        ? "linear-gradient(145deg, rgba(18,18,19,0.9) 0%, rgba(26,26,28,0.9) 100%)"
        : "#ffffff",
    borderRadius: 20,
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(91,124,255,0.15)"
        : "1px solid #e2e8f0",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 32px rgba(0,0,0,0.4)"
        : "0 4px 6px rgba(0,0,0,0.05)",
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1.5),
    transition: "all 0.3s ease",
    "&:hover": {
      border:
        theme.palette.type === "dark"
          ? "1px solid rgba(91,124,255,0.3)"
          : "1px solid #cbd5e1",
      transform: "translateY(-2px)",
    },
  },
  cardIcon: {
    fontSize: "2rem",
    lineHeight: 1,
  },
  cardLabel: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
  },
  cardMain: {
    display: "flex",
    alignItems: "baseline",
    gap: 6,
  },
  cardValue: {
    fontSize: "2.8rem",
    fontWeight: 800,
    lineHeight: 1,
  },
  cardTotal: {
    fontSize: "1rem",
    color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
    fontWeight: 500,
  },
  cardBtn: {
    marginTop: theme.spacing(1),
    background: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 4,
    color: theme.palette.type === "dark" ? "#a5b4fc" : "#3b82f6",
    fontSize: "0.82rem",
    fontWeight: 600,
    padding: 0,
    fontFamily: "'Montserrat', sans-serif",
    "&:hover": { opacity: 0.75 },
  },
  refreshTime: {
    fontSize: "0.75rem",
    color: theme.palette.type === "dark" ? "#6b7280" : "#94a3b8",
    marginRight: theme.spacing(1),
  },
  // Modal
  dialogPaper: {
    background:
      theme.palette.type === "dark" ? "#121213" : "#ffffff",
    borderRadius: 16,
    minWidth: 680,
    [theme.breakpoints.down("sm")]: { minWidth: "90vw" },
  },
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottom:
      theme.palette.type === "dark"
        ? "1px solid rgba(255,255,255,0.06)"
        : "1px solid #f1f5f9",
    padding: theme.spacing(2, 3),
  },
  dialogTitleText: {
    fontSize: "1.1rem",
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#ffffff" : "#1e293b",
  },
  searchField: {
    marginBottom: theme.spacing(2),
    "& .MuiOutlinedInput-root": { borderRadius: 10 },
  },
  table: {
    "& .MuiTableCell-head": {
      fontSize: "0.72rem",
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      color: theme.palette.type === "dark" ? "#9ca3af" : "#64748b",
      background:
        theme.palette.type === "dark"
          ? "rgba(255,255,255,0.03)"
          : "#f8fafc",
      borderBottom:
        theme.palette.type === "dark"
          ? "1px solid rgba(255,255,255,0.06)"
          : "1px solid #e2e8f0",
    },
    "& .MuiTableCell-body": {
      fontSize: "0.85rem",
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#374151",
      borderBottom:
        theme.palette.type === "dark"
          ? "1px solid rgba(255,255,255,0.04)"
          : "1px solid #f1f5f9",
    },
  },
  chipConnected: {
    background: "rgba(34,197,94,0.15)",
    color: "#16a34a",
    fontWeight: 600,
    fontSize: "0.7rem",
    height: 22,
  },
  chipDisconnected: {
    background:
      "rgba(156,163,175,0.15)",
    color: "#6b7280",
    fontWeight: 600,
    fontSize: "0.7rem",
    height: 22,
  },
  chipOpening: {
    background: "rgba(245,158,11,0.15)",
    color: "#d97706",
    fontWeight: 600,
    fontSize: "0.7rem",
    height: 22,
  },
  chipProvider: {
    fontWeight: 600,
    fontSize: "0.68rem",
    height: 20,
  },
  emptyRow: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.type === "dark" ? "#6b7280" : "#9ca3af",
  },
  actionBtn: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: "0.75rem",
    borderRadius: 8,
    padding: "2px 10px",
    whiteSpace: "nowrap",
  },
}));

const providerLabel = (provider) => {
  if (provider === "meta") return { label: "API Oficial", color: "#2563eb", bg: "rgba(37,99,235,0.12)" };
  if (provider === "waha") return { label: "WAHA", color: "#7c3aed", bg: "rgba(124,58,237,0.12)" };
  return { label: "API Oficial", color: "#2563eb", bg: "rgba(37,99,235,0.12)" };
};

const formatPhone = (raw) => {
  if (!raw) return "—";
  let digits = String(raw).replace(/\D/g, "");
  if (!digits) return "—";
  if (digits.startsWith("0")) digits = digits.substring(1);
  if (!digits.startsWith("55")) digits = "55" + digits;
  if (digits.length < 12 || digits.length > 13) return `+${digits}`;
  const ddd = digits.substring(2, 4);
  const subscriber = digits.substring(4);
  const firstPart = subscriber.slice(0, -4);
  const lastPart = subscriber.slice(-4);
  return `+55 (${ddd}) ${firstPart}-${lastPart}`;
};

const statusInfo = (status, classes) => {
  const s = (status || "").toUpperCase();
  if (s === "CONNECTED" || s === "WORKING") {
    return { label: "Conectada", className: classes.chipConnected };
  }
  if (s === "OPENING" || s === "STARTING") {
    return { label: "Abrindo", className: classes.chipOpening };
  }
  if (s === "PAIRING" || s === "QRCODE" || s === "SCAN_QR_CODE") {
    return { label: "Aguardando QR", className: classes.chipOpening };
  }
  if (s === "TIMEOUT") {
    return { label: "Timeout", className: classes.chipDisconnected };
  }
  if (s === "ERROR") {
    return { label: "Erro", className: classes.chipDisconnected };
  }
  if (s === "DISCONNECTED") {
    return { label: "Desconectada", className: classes.chipDisconnected };
  }
  return { label: status || "—", className: classes.chipDisconnected };
};

const SuperAdminPanel = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [connections, setConnections] = useState({ active: 0, total: 0, connections: [] });
  const [onlineUsers, setOnlineUsers] = useState({ online: 0, total: 0, users: [] });
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);

  const [connectionsModal, setConnectionsModal] = useState(false);
  const [usersModal, setUsersModal] = useState(false);
  const [connSearch, setConnSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [connSort, setConnSort] = useState({ field: "name", dir: "asc" });

  // Ação sobre uma conexão: reinicia (usa ConfirmationModal) ou mostra QR.
  const [confirmAction, setConfirmAction] = useState({
    open: false,
    conn: null,
    loading: false,
  });
  const [qrModal, setQrModal] = useState({ open: false, whatsAppId: null });

  useEffect(() => { loadMontserratFont(); }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [connRes, usersRes, companiesRes] = await Promise.all([
        api.get("/super-dashboard/connections"),
        api.get("/super-dashboard/online-users"),
        api.get("/super-dashboard/companies"),
      ]);
      setConnections(connRes.data);
      setOnlineUsers(usersRes.data);
      setCompanies(companiesRes.data);
      setLastUpdate(new Date());
    } catch (err) {
      toast.error("Erro ao carregar dados do Admin Panel");
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const interval = setInterval(fetchAll, 30000);
    return () => clearInterval(interval);
  }, [fetchAll]);

  const handleConnSort = (field) => {
    setConnSort((prev) =>
      prev.field === field
        ? { field, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { field, dir: "asc" }
    );
  };

  const filteredConnections = useMemo(() => {
    const q = connSearch.toLowerCase();
    const base = (connections.connections || []).filter(
      (c) =>
        !q ||
        (c.name || "").toLowerCase().includes(q) ||
        (c.number || "").toLowerCase().includes(q) ||
        (c.companyName || "").toLowerCase().includes(q)
    );
    const { field, dir } = connSort;
    const mult = dir === "asc" ? 1 : -1;
    const get = (c) => {
      if (field === "provider") return (c.provider || "").toLowerCase();
      if (field === "status") return (c.status || "").toLowerCase();
      if (field === "companyName") return (c.companyName || "").toLowerCase();
      if (field === "number") return (c.number || "").toLowerCase();
      if (field === "updatedAt") {
        return c.updatedAt ? new Date(c.updatedAt).getTime() : 0;
      }
      return (c.name || "").toLowerCase();
    };
    return [...base].sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
  }, [connections.connections, connSearch, connSort]);

  const formatSince = (iso) => {
    if (!iso) return "—";
    try {
      return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
    } catch {
      return "—";
    }
  };

  // Classifica o status em "precisa reiniciar" / "aguardando QR" / "ok".
  const connActionKind = (status) => {
    const s = (status || "").toLowerCase();
    if (s.includes("qr") || s === "pairing") return "qr";
    if (s === "connected" || s === "working") return "ok";
    return "restart";
  };

  const handleOpenConfirmRestart = (conn) => {
    setConfirmAction({ open: true, conn, loading: false });
  };
  const handleCloseConfirmRestart = () => {
    setConfirmAction({ open: false, conn: null, loading: false });
  };

  const handleConfirmRestart = async () => {
    const conn = confirmAction.conn;
    if (!conn) return;
    setConfirmAction((prev) => ({ ...prev, loading: true }));
    try {
      await api.post(`/whatsapp/${conn.id}/restart`);
      toast.success("Sessão reiniciada. Aguarde atualização do status.");
      setConfirmAction({ open: false, conn: null, loading: false });
      fetchAll();
    } catch (err) {
      toastError(err);
      setConfirmAction((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleOpenQr = (conn) => {
    setQrModal({ open: true, whatsAppId: conn.id });
  };
  const handleCloseQr = () => {
    setQrModal({ open: false, whatsAppId: null });
  };

  const filteredUsers = (onlineUsers.users || []).filter((u) => {
    const q = userSearch.toLowerCase();
    return (
      !q ||
      (u.name || "").toLowerCase().includes(q) ||
      (u.email || "").toLowerCase().includes(q) ||
      (u.companyName || "").toLowerCase().includes(q)
    );
  });

  if (!user?.super) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <Typography variant="h6">Acesso não autorizado</Typography>
      </Box>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Box>
          <Typography className={classes.title}>Admin Panel</Typography>
          <Typography className={classes.subtitle}>Visão geral de toda a plataforma</Typography>
        </Box>
        <Box display="flex" alignItems="center">
          {lastUpdate && (
            <Typography className={classes.refreshTime}>
              Atualizado: {lastUpdate.toLocaleTimeString("pt-BR")}
            </Typography>
          )}
          <Tooltip title="Atualizar">
            <IconButton onClick={fetchAll} disabled={loading} size="small">
              <RefreshCw size={20} style={{ animation: loading ? "spin 1s linear infinite" : "none" }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Cards */}
      <Box className={classes.cardsGrid}>
        {/* Conexões */}
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>📡 Conexões</Typography>
          <Box className={classes.cardMain}>
            <Typography
              className={classes.cardValue}
              style={{ color: "#3b82f6" }}
            >
              {connections.active}
            </Typography>
            <Typography className={classes.cardTotal}>
              ativas de {connections.total}
            </Typography>
          </Box>
          <button
            className={classes.cardBtn}
            onClick={() => setConnectionsModal(true)}
          >
            Ver todas →
          </button>
        </Box>

        {/* Usuários online */}
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>👥 Usuários online</Typography>
          <Box className={classes.cardMain}>
            <Typography
              className={classes.cardValue}
              style={{ color: "#22c55e" }}
            >
              {onlineUsers.online}
            </Typography>
            <Typography className={classes.cardTotal}>
              online de {onlineUsers.total}
            </Typography>
          </Box>
          <button
            className={classes.cardBtn}
            onClick={() => setUsersModal(true)}
          >
            Ver todos →
          </button>
        </Box>

        {/* Empresas */}
        <Box className={classes.card}>
          <Typography className={classes.cardLabel}>🏢 Empresas</Typography>
          <Box className={classes.cardMain}>
            <Typography
              className={classes.cardValue}
              style={{ color: "#f59e0b" }}
            >
              {companies.length}
            </Typography>
            <Typography className={classes.cardTotal}>cadastradas</Typography>
          </Box>
        </Box>
      </Box>

      {/* Dashboard de mensagens (Não oficial + API Oficial) — visão cross-tenant */}
      <MessageStats />

      {/* Modal Conexões */}
      <Dialog
        open={connectionsModal}
        onClose={() => setConnectionsModal(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogTitle}>
          <Typography className={classes.dialogTitleText}>
            Todas as conexões ({connections.total})
          </Typography>
          <IconButton size="small" onClick={() => setConnectionsModal(false)}>
            <X size={18} />
          </IconButton>
        </Box>
        <DialogContent style={{ padding: "20px 24px" }}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Buscar por nome, número ou empresa..."
            value={connSearch}
            onChange={(e) => setConnSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                {[
                  { id: "name", label: "Nome" },
                  { id: "number", label: "Número" },
                  { id: "companyName", label: "Empresa" },
                  { id: "provider", label: "Tipo" },
                  { id: "status", label: "Status" },
                  { id: "updatedAt", label: "Ativa há" },
                ].map((col) => (
                  <TableCell
                    key={col.id}
                    sortDirection={connSort.field === col.id ? connSort.dir : false}
                  >
                    <TableSortLabel
                      active={connSort.field === col.id}
                      direction={connSort.field === col.id ? connSort.dir : "asc"}
                      onClick={() => handleConnSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredConnections.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className={classes.emptyRow}>
                    Nenhuma conexão encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredConnections.map((c) => {
                  const pv = providerLabel(c.provider);
                  const st = statusInfo(c.status, classes);
                  const kind = connActionKind(c.status);
                  return (
                    <TableRow key={c.id} hover>
                      <TableCell>{c.name || "—"}</TableCell>
                      <TableCell>{formatPhone(c.number)}</TableCell>
                      <TableCell>{c.companyName || "—"}</TableCell>
                      <TableCell>
                        <Chip
                          label={pv.label}
                          size="small"
                          className={classes.chipProvider}
                          style={{ background: pv.bg, color: pv.color }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={st.label}
                          size="small"
                          className={st.className}
                        />
                      </TableCell>
                      <TableCell>{formatSince(c.updatedAt)}</TableCell>
                      <TableCell align="right">
                        {kind === "ok" ? (
                          <span style={{ color: "#9ca3af" }}>—</span>
                        ) : kind === "qr" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<CropFreeIcon fontSize="small" />}
                            onClick={() => handleOpenQr(c)}
                            className={classes.actionBtn}
                          >
                            Ver QR
                          </Button>
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<SignalWifiOffIcon fontSize="small" />}
                            onClick={() => handleOpenConfirmRestart(c)}
                            className={classes.actionBtn}
                          >
                            Reiniciar
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      {/* Confirmação de reiniciar conexão */}
      <ConfirmationModal
        title={
          confirmAction.conn
            ? `Reiniciar conexão ${confirmAction.conn.name || ""}?`
            : "Reiniciar conexão?"
        }
        open={confirmAction.open}
        onClose={handleCloseConfirmRestart}
        onConfirm={handleConfirmRestart}
        confirmLoading={confirmAction.loading}
      >
        A conexão ficará temporariamente offline enquanto reinicia. Mensagens
        novas podem chegar com atraso até o WhatsApp reconectar.
      </ConfirmationModal>

      {/* Modal de QR code (reusa componente de Connections) */}
      {qrModal.open && (
        <QrcodeModal
          open={qrModal.open}
          onClose={handleCloseQr}
          whatsAppId={qrModal.whatsAppId}
        />
      )}

      {/* Modal Usuários Online */}
      <Dialog
        open={usersModal}
        onClose={() => setUsersModal(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ className: classes.dialogPaper }}
      >
        <Box className={classes.dialogTitle}>
          <Typography className={classes.dialogTitleText}>
            Usuários online agora ({onlineUsers.online})
          </Typography>
          <IconButton size="small" onClick={() => setUsersModal(false)}>
            <X size={18} />
          </IconButton>
        </Box>
        <DialogContent style={{ padding: "20px 24px" }}>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            fullWidth
            placeholder="Buscar por nome, email ou empresa..."
            value={userSearch}
            onChange={(e) => setUserSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={18} style={{ color: "#9ca3af" }} />
                </InputAdornment>
              ),
            }}
          />
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Perfil</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className={classes.emptyRow}>
                    Nenhum usuário online agora
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((u) => (
                  <TableRow key={u.id} hover>
                    <TableCell>{u.name || "—"}</TableCell>
                    <TableCell>{u.email || "—"}</TableCell>
                    <TableCell>{u.companyName || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        label={u.profile === "admin" ? "Admin" : "Atendente"}
                        size="small"
                        className={classes.chipProvider}
                        style={
                          u.profile === "admin"
                            ? { background: "rgba(239,68,68,0.12)", color: "#dc2626" }
                            : { background: "rgba(107,114,128,0.12)", color: "#6b7280" }
                        }
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </DialogContent>
      </Dialog>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
};

export default SuperAdminPanel;
